/**
 * "Completá tu rutina": fila deslizable de productos recomendados.
 * Config: "Recomendaciones" en la configuración del tema.
 *
 * Contextos (data-context):
 *  - cart:    dentro del carrito Vue, fija abajo arriba del total.
 *             Recomienda en base a los productos del carrito.
 *  - product: en la PDP, en base al producto (data-product-id). Los curados
 *             vienen inline en <script data-curated>.
 *
 * Fuente: primero los curados a mano (metafield de cross selling) y después
 * las recomendaciones de Shopify (complementarias de Search & Discovery y, si
 * no hay, las relacionadas). Sin productos del carrito ni agotados.
 *
 * Si hay metas activas, cada producto muestra qué desbloquea al sumarlo.
 */
(() => {
  const CFG = window.TCL_RECS;
  const P = window.TCLProduct;
  const watch = window.TCLCartWatch;
  if (!CFG || !P || !watch || customElements.get('product-recs')) return;

  const { escapeHtml } = P;
  const cache = new Map();

  const fetchRecs = (productId, intent) => {
    const key = `${productId}:${intent}`;
    if (!cache.has(key)) {
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products?section_id=tcl-recommendations&product_id=${productId}&limit=${CFG.limit + 4}&intent=${intent}`;
      cache.set(
        key,
        fetch(url)
          .then((res) => (res.ok ? res.text() : ''))
          .then((html) => {
            const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tcl-recs]');
            return node ? JSON.parse(node.textContent) : { curated: [], recommended: [] };
          })
          .catch(() => ({ curated: [], recommended: [] }))
      );
    }
    return cache.get(key);
  };

  // Curados del carrito + complementarias (o relacionadas si no hay).
  const loadFor = async (productId) => {
    const complementary = await fetchRecs(productId, 'complementary');
    if (complementary.recommended.length) return complementary;
    const related = await fetchRecs(productId, 'related');
    return { curated: complementary.curated, recommended: related.recommended };
  };

  // Card horizontal: imagen, nombre, precio, beneficio y botón AGREGAR/ELEGIR.
  const cardHtml = (product, gain) => {
    const single = product.variants.length === 1;
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const action = single
      ? `data-tcl-add="${variant.id}" data-message="${escapeHtml(product.message)}"`
      : `data-tcl-quick-view="${escapeHtml(product.url)}"`;
    const compare = product.compareAtPrice > product.price ? `<s>${escapeHtml(P.money(product.compareAtPrice))}</s>` : '';
    return `
      <li class="recs__item">
        <a class="recs__media" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">
          ${product.image ? `<img src="${escapeHtml(product.image)}" alt="" loading="lazy" width="150" height="150">` : ''}
        </a>
        <div class="recs__body">
          <a class="recs__title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <p class="recs__price">${product.priceVaries ? 'Desde ' : ''}<strong>${escapeHtml(P.money(product.price))}</strong> ${compare}</p>
          ${gain ? `<span class="recs__gain">Sumalo: ${escapeHtml(gain)}</span>` : ''}
        </div>
        <button type="button" class="recs__add" ${action}
          aria-label="${escapeHtml(single ? `${CFG.addLabel}: ${product.title}` : `${CFG.chooseLabel}: ${product.title}`)}">
          <span>${escapeHtml(single ? CFG.addLabel : CFG.chooseLabel)}</span>
        </button>
      </li>`;
  };

  class ProductRecs extends HTMLElement {
    connectedCallback() {
      this.context = this.dataset.context;
      if (this.context === 'product') {
        try {
          this.curated = JSON.parse(this.querySelector('[data-curated]')?.textContent || '[]');
        } catch (e) {
          this.curated = [];
        }
        loadFor(this.dataset.productId).then((data) => {
          this.data = { curated: this.curated, recommended: data.recommended };
          if (watch.cart) this.render(watch.cart);
        });
      }
      this.unsubscribe = watch.subscribe((cart) => this.onCart(cart));
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    async onCart(cart) {
      if (this.context === 'cart') {
        // Recalcula solo si cambiaron los productos del carrito.
        const ids = cart.items.filter((i) => !watch.isGift(i)).map((i) => i.product_id);
        const key = [...new Set(ids)].join(',');
        if (key !== this.cartKey) {
          this.cartKey = key;
          this.data = ids.length ? await loadFor(ids[0]) : null;
        }
      }
      this.render(cart);
    }

    render(cart) {
      if (!this.data) {
        this.hidden = true;
        return;
      }
      const exclude = new Set(cart.items.map((i) => i.product_id));
      if (this.dataset.productId) exclude.add(Number(this.dataset.productId));
      const seen = new Set();
      const products = [...this.data.curated, ...this.data.recommended]
        .filter((p) => p.available && !exclude.has(p.id) && !seen.has(p.id) && seen.add(p.id))
        .slice(0, CFG.limit);

      this.hidden = !products.length;
      if (!products.length) return;

      const gainFor = window.TCLCartGoals?.gainFor;
      const expanded = this.classList.contains('is-expanded');
      // En el carrito: flecha para pasar de fila horizontal a lista vertical.
      const expandToggle =
        this.context === 'cart' && products.length > 1
          ? `<button type="button" class="recs__expand" aria-expanded="${expanded}" aria-label="${expanded ? 'Ver menos' : 'Ver todos'}">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 15l6-6 6 6"/></svg>
            </button>`
          : '';
      this.innerHTML = `
        <div class="recs__head">
          <h3 class="recs__heading">${escapeHtml(CFG.title)}</h3>
          <div class="recs__actions">
            <div class="recs__nav" aria-hidden="true">
              <button type="button" class="recs__arrow" data-dir="-1" tabindex="-1">&#8592;</button>
              <button type="button" class="recs__arrow" data-dir="1" tabindex="-1">&#8594;</button>
            </div>
            ${expandToggle}
          </div>
        </div>
        <ul class="recs__track">
          ${products.map((p) => cardHtml(p, gainFor ? gainFor(cart, p.price) : null)).join('')}
        </ul>`;

      const track = this.querySelector('.recs__track');
      // Sin volver a dibujar: si el botón tocado sale del DOM, el carrito Vue
      // toma el click como "afuera" y se cierra.
      this.querySelector('.recs__expand')?.addEventListener('click', (e) => {
        const expanded = this.classList.toggle('is-expanded');
        e.currentTarget.setAttribute('aria-expanded', expanded);
        e.currentTarget.setAttribute('aria-label', expanded ? 'Ver menos' : 'Ver todos');
      });
      this.querySelectorAll('.recs__arrow').forEach((arrow) =>
        arrow.addEventListener('click', () =>
          track.scrollBy({ left: Number(arrow.dataset.dir) * track.clientWidth * 0.8, behavior: 'smooth' })
        )
      );
    }
  }

  customElements.define('product-recs', ProductRecs);

  // --- Montaje dentro del carrito Vue --------------------------------------
  // Fija abajo, justo arriba del total (.cart--footer), como hijo directo del
  // carrito. Si Vue mueve algo, se vuelve a ubicar.

  if (CFG.inCart) {
    let el;
    const mount = () => {
      const footer = document.querySelector('#ModalCart .cart--footer');
      if (!footer) return;
      if (!el) {
        el = document.createElement('product-recs');
        el.className = 'recs recs--cart';
        el.dataset.context = 'cart';
        el.hidden = true;
      }
      if (el.nextElementSibling !== footer) footer.parentNode.insertBefore(el, footer);
    };

    const start = () => {
      const root = document.getElementById('ModalCart');
      if (!root) return;
      mount();
      new MutationObserver(mount).observe(root, { childList: true, subtree: true });
    };

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }
})();
